import * as React from 'react';
import { MonitoringIcon, SearchIcon, TimesIcon, ExclamationCircleIcon, MapMarkerIcon, UsersIcon, CheckCircleIcon, RouteIcon, PlusCircleIcon } from '@patternfly/react-icons';
import { getAds, getCampaigns, deleteCampaign, stopCampaign, getOrganizations, getCampaignRuns, CampaignRun } from "@app/utils/utils";
import toast from 'react-hot-toast';
import { useEffect, useState, useRef } from "react";
import { PageSection, EmptyState, EmptyStateIcon, EmptyStateBody, EmptyStateVariant, Title } from "@patternfly/react-core";
import { CreateCampaign } from './CreateCampaign';
import { EditCampaign } from './EditCampaign';
import { CampaignCard } from './CampaignCard';
import { getCampaignTypeConfig } from './campaignTypeUtils';
import { useNavigate, Link } from "react-router-dom";

export interface ICampaignsProps {}

const Campaigns: React.FunctionComponent<ICampaignsProps> = (props) => {

    const [campaignState, setCampaignState] = useState<any[]>([]);
    const [triggerRefresh, setTriggerRefresh] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [hasAds, setHasAds] = useState(true);
    const navigate = useNavigate();

    // Search and filter state
    const [searchText, setSearchText] = useState('');
    const [typeFilter, setTypeFilter] = useState<string | null>(null);
    const [stateFilters, setStateFilters] = useState<string[]>([]);
    const [showStateDropdown, setShowStateDropdown] = useState(false);
    const stateDropdownRef = useRef<HTMLDivElement>(null);

    // Edit modal state
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState<any>(null);

    // Error modal state
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    // Create campaign modal state (for empty state)
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [initialCampaignType, setInitialCampaignType] = useState<string | undefined>(undefined);

    // Create ad first modal state
    const [showCreateAdModal, setShowCreateAdModal] = useState(false);

    const TERMINAL_STATES = ['STOPPED_BY_OWNER'];

    // Handle campaign card click - check if user has ads first
    const handleCreateCampaignClick = (campaignType: string) => {
        if (hasAds) {
            setInitialCampaignType(campaignType);
            setCreateModalOpen(true);
        } else {
            setShowCreateAdModal(true);
        }
    };

    const fetchCampaigns = () => {
        getCampaigns().then(function(result) {
            setCampaignState(result || []);
            setIsLoading(false);
        });
    };

    // Check if user has organizations - redirect if not
    useEffect(() => {
        getOrganizations().then(function(result) {
            if (result == undefined || result == null || !result.organizations || result.organizations.length == 0) {
                navigate('/organizations');
            }
        });
    }, []);

    useEffect(() => {
        setIsLoading(true);
        // Check if user has ads
        getAds().then(function(result) {
            if (result == undefined || result == null || result.length == 0) {
                setHasAds(false);
            } else {
                setHasAds(true);
            }
        });
        // Load campaigns, then seed the "last seen NO_DRIVER_AVAILABLE run ids" map for
        // every non-terminal campaign so we don't toast for refunds that existed before
        // the user opened the page.
        getCampaigns().then(async (result) => {
            setCampaignState(result || []);
            setIsLoading(false);
            if (!result) return;
            const lastSeen = (() => {
                try {
                    const raw = localStorage.getItem('campaignRuns:lastSeenNoDriverRunIds');
                    return raw ? JSON.parse(raw) : {};
                } catch { return {}; }
            })();
            const updated: Record<string, number[]> = { ...lastSeen };
            const nonTerminal = result.filter(
                (c: any) => c.currentState && !TERMINAL_STATES.includes(c.currentState)
            );
            for (const c of nonTerminal) {
                if (updated[c.campaignId] !== undefined) continue; // already seeded
                try {
                    const runs: CampaignRun[] = await getCampaignRuns(c.campaignId);
                    updated[c.campaignId] = runs
                        .filter((r) => r.approvalState === 'NO_DRIVER_AVAILABLE')
                        .map((r) => (r.runId ?? r.id) as number)
                        .filter((id) => typeof id === 'number');
                } catch {
                    /* skip */
                }
            }
            try {
                localStorage.setItem('campaignRuns:lastSeenNoDriverRunIds', JSON.stringify(updated));
            } catch { /* quota */ }
        });
    }, [triggerRefresh]);

    // Poll for campaign status changes when any campaign is in a non-terminal state.
    // Also poll per-campaign runs and diff for newly NO_DRIVER_AVAILABLE refunds so we can
    // surface a one-shot toast (dismissed toasts are not re-fired thanks to localStorage).
    const REFUND_LS_KEY = 'campaignRuns:lastSeenNoDriverRunIds';
    const loadLastSeen = (): Record<string, number[]> => {
        try {
            const raw = localStorage.getItem(REFUND_LS_KEY);
            return raw ? JSON.parse(raw) : {};
        } catch {
            return {};
        }
    };
    const saveLastSeen = (next: Record<string, number[]>) => {
        try {
            localStorage.setItem(REFUND_LS_KEY, JSON.stringify(next));
        } catch {
            /* quota — non-fatal */
        }
    };

    useEffect(() => {
        const hasNonTerminal = campaignState.some(
            c => c.currentState && !TERMINAL_STATES.includes(c.currentState)
        );
        if (!hasNonTerminal) return;

        const pollOnce = async () => {
            fetchCampaigns();
            // Refund diff: fetch runs for non-terminal campaigns and toast on new NO_DRIVER_AVAILABLE rows.
            const nonTerminal = campaignState.filter(
                (c) => c.currentState && !TERMINAL_STATES.includes(c.currentState)
            );
            if (nonTerminal.length === 0) return;
            const lastSeen = loadLastSeen();
            const updated: Record<string, number[]> = { ...lastSeen };
            for (const c of nonTerminal) {
                try {
                    const runs: CampaignRun[] = await getCampaignRuns(c.campaignId);
                    const noDriverIds = runs
                        .filter((r) => r.approvalState === 'NO_DRIVER_AVAILABLE')
                        .map((r) => (r.runId ?? r.id) as number)
                        .filter((id) => typeof id === 'number');
                    const prevSeen = new Set(lastSeen[c.campaignId] || []);
                    const fresh = noDriverIds.filter((id) => !prevSeen.has(id));
                    if (fresh.length > 0) {
                        const cName = c.name || `Campaign ${c.campaignId}`;
                        if (fresh.length === 1) {
                            toast.error(
                                `${cName}: a slot couldn't be filled. 1 credit refunded.`,
                                { duration: 7000 }
                            );
                        } else {
                            toast.error(
                                `${cName}: ${fresh.length} slots couldn't be filled. ${fresh.length} credits refunded.`,
                                { duration: 7000 }
                            );
                        }
                    }
                    updated[c.campaignId] = noDriverIds;
                } catch {
                    /* keep stale */
                }
            }
            saveLastSeen(updated);
        };

        const interval = setInterval(pollOnce, 15000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignState]);

    // Close state dropdown when clicking outside
    useEffect(function() {
        function handleClickOutside(event: MouseEvent) {
            if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target as Node)) {
                setShowStateDropdown(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return function() {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleEditClick = (campaign: any) => {
        setSelectedCampaign(campaign);
        setEditModalOpen(true);
    };

    const handleEditClose = () => {
        setEditModalOpen(false);
        setSelectedCampaign(null);
    };

    const handleEditSave = () => {
        setTriggerRefresh(!triggerRefresh);
    };

    const handleCardClick = (campaign: any) => {
        navigate('/campaigns/' + campaign.campaignId);
    };

    const handleDeleteClick = async (campaign: any) => {
        try {
            await deleteCampaign(campaign.campaignId);
            // Remove the deleted campaign from local state immediately
            setCampaignState(prevCampaigns => prevCampaigns.filter(c => c.campaignId !== campaign.campaignId));
        } catch (error) {
            console.error('Failed to delete campaign:', error);
            setErrorMessage(`Failed to delete "${campaign.name}". Please try again.`);
            setShowErrorModal(true);
        }
    };

    const handleStopClick = async (campaign: any) => {
        try {
            await stopCampaign(campaign.campaignId);
            // Update local state: change campaign's currentState to 'STOPPED_BY_OWNER'
            setCampaignState(prevCampaigns => prevCampaigns.map(c =>
                c.campaignId === campaign.campaignId
                    ? { ...c, currentState: 'STOPPED_BY_OWNER' }
                    : c
            ));
        } catch (error) {
            console.error('Failed to stop campaign:', error);
            setErrorMessage(`Failed to stop "${campaign.name}". Please try again.`);
            setShowErrorModal(true);
        }
    };

    // Filter campaigns based on search text, type, and state
    const filteredCampaigns = campaignState.filter(function(campaign) {
        var searchLower = searchText.toLowerCase();
        var matchesSearch = searchText === '' ||
            (campaign.name && campaign.name.toLowerCase().indexOf(searchLower) !== -1) ||
            (campaign.organizationName && campaign.organizationName.toLowerCase().indexOf(searchLower) !== -1);

        var matchesType = typeFilter === null ||
            (campaign.campaignType && campaign.campaignType === typeFilter);

        var matchesState = stateFilters.length === 0 ||
            (campaign.currentState && stateFilters.indexOf(campaign.currentState) !== -1);

        return matchesSearch && matchesType && matchesState;
    }).sort(function(a, b) {
        var aTime = new Date(a.updateDateTime || a.createDateTime || 0).getTime();
        var bTime = new Date(b.updateDateTime || b.createDateTime || 0).getTime();
        return bTime - aTime;
    });

    // Define the campaign type options with their display labels
    const campaignTypeOptions = [
        { value: 'GeoFenceCampaign', label: 'Geo Fence' },
        { value: 'DrawRoutesCampaign', label: 'Draw Route' },
        { value: 'TargetReachCampaign', label: 'AI Route' }
    ];

    // Define the campaign state options
    const campaignStateOptions = [
        { value: 'DRAFT', label: 'Draft' },
        { value: 'SCHEDULED', label: 'Scheduled' },
        { value: 'ACTIVE', label: 'Active' },
        { value: 'COMPLETED', label: 'Completed' },
        { value: 'PARTIALLY_COMPLETE', label: 'Partially complete' },
        { value: 'NEEDS_RUNS', label: 'Needs runs' },
        { value: 'STOPPED_BY_OWNER', label: 'Stopped' },
        { value: 'ERROR', label: 'Error' }
    ];

    // Toggle a state filter
    const toggleStateFilter = function(state: string) {
        setStateFilters(function(prev) {
            if (prev.indexOf(state) !== -1) {
                return prev.filter(function(s) { return s !== state; });
            } else {
                return prev.concat([state]);
            }
        });
    };

    const styles = {
        container: {
            padding: '24px'
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '32px'
        },
        title: {
            fontSize: '28px',
            fontWeight: 700,
            color: '#1f2937',
            margin: 0
        },
        subtitle: {
            fontSize: '14px',
            color: '#6b7280',
            marginTop: '4px'
        },
        grid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '24px'
        },
        emptyStateContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px'
        },
        searchContainer: {
            marginBottom: '24px'
        },
        searchRow: {
            display: 'flex',
            gap: '16px',
            alignItems: 'center',
            flexWrap: 'wrap' as const
        },
        searchInputWrapper: {
            position: 'relative' as const,
            flex: '1',
            minWidth: '250px',
            maxWidth: '400px'
        },
        searchInput: {
            width: '100%',
            padding: '12px 40px 12px 44px',
            fontSize: '14px',
            border: '2px solid #e5e7eb',
            borderRadius: '10px',
            outline: 'none',
            transition: 'border-color 0.2s, box-shadow 0.2s',
            background: '#ffffff'
        },
        searchIcon: {
            position: 'absolute' as const,
            left: '14px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#9ca3af',
            pointerEvents: 'none' as const
        },
        clearButton: {
            position: 'absolute' as const,
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: '#e5e7eb',
            border: 'none',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#6b7280',
            padding: 0
        },
        filterChips: {
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap' as const
        },
        filterChip: {
            padding: '8px 16px',
            fontSize: '13px',
            fontWeight: 500,
            border: '2px solid #e5e7eb',
            borderRadius: '20px',
            background: '#ffffff',
            color: '#6b7280',
            cursor: 'pointer',
            transition: 'all 0.2s'
        },
        filterChipActive: {
            padding: '8px 16px',
            fontSize: '13px',
            fontWeight: 600,
            border: '2px solid #667eea',
            borderRadius: '20px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#ffffff',
            cursor: 'pointer',
            transition: 'all 0.2s'
        },
        stateDropdownContainer: {
            position: 'relative' as const
        },
        stateDropdownButton: {
            padding: '8px 16px',
            fontSize: '13px',
            fontWeight: 500,
            border: '2px solid #e5e7eb',
            borderRadius: '10px',
            background: '#ffffff',
            color: '#374151',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s'
        },
        stateDropdownButtonActive: {
            borderColor: '#667eea',
            background: '#f5f3ff'
        },
        stateDropdownMenu: {
            position: 'absolute' as const,
            top: '100%',
            left: 0,
            marginTop: '4px',
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '10px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
            zIndex: 100,
            minWidth: '180px',
            padding: '8px 0'
        },
        stateDropdownItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '10px 16px',
            fontSize: '13px',
            color: '#374151',
            cursor: 'pointer',
            transition: 'background 0.15s'
        },
        stateCheckbox: {
            width: '16px',
            height: '16px',
            borderRadius: '4px',
            border: '2px solid #d1d5db',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
        },
        stateCheckboxChecked: {
            background: '#667eea',
            borderColor: '#667eea',
            color: '#ffffff'
        },
        resultsInfo: {
            fontSize: '13px',
            color: '#6b7280',
            marginTop: '12px'
        },
        // Error modal styles
        errorOverlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
        },
        errorModal: {
            background: '#ffffff',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
        },
        errorIcon: {
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: '#fef2f2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            color: '#dc2626'
        },
        errorTitle: {
            fontSize: '18px',
            fontWeight: 700,
            color: '#1f2937',
            margin: '0 0 8px 0',
            textAlign: 'center' as const
        },
        errorText: {
            fontSize: '14px',
            color: '#6b7280',
            margin: '0 0 20px 0',
            lineHeight: 1.5,
            textAlign: 'center' as const
        },
        errorButton: {
            width: '100%',
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 600,
            border: 'none',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#ffffff',
            cursor: 'pointer'
        },
        // Create ad first modal styles
        createAdOverlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
        },
        createAdModal: {
            background: '#ffffff',
            borderRadius: '16px',
            maxWidth: '420px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            overflow: 'hidden'
        },
        createAdHeader: {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '24px',
            textAlign: 'center' as const
        },
        createAdIconContainer: {
            width: '64px',
            height: '64px',
            borderRadius: '16px',
            background: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            color: '#ffffff',
            fontSize: '28px'
        },
        createAdTitle: {
            fontSize: '22px',
            fontWeight: 700,
            color: '#ffffff',
            margin: 0
        },
        createAdBody: {
            padding: '24px',
            textAlign: 'center' as const
        },
        createAdText: {
            fontSize: '15px',
            color: '#4b5563',
            margin: '0 0 24px 0',
            lineHeight: 1.6
        },
        createAdLink: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 28px',
            fontSize: '15px',
            fontWeight: 600,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#ffffff',
            borderRadius: '10px',
            textDecoration: 'none',
            transition: 'all 0.2s',
            boxShadow: '0 4px 14px rgba(102, 126, 234, 0.4)'
        },
        createAdCancel: {
            display: 'block',
            marginTop: '16px',
            fontSize: '14px',
            color: '#6b7280',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '8px'
        }
    };

    const emptyStateStyles = {
        wrapper: {
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            padding: '40px 20px'
        },
        header: {
            textAlign: 'center' as const,
            marginBottom: '40px'
        },
        mainTitle: {
            fontSize: '32px',
            fontWeight: 700,
            color: '#1f2937',
            margin: '0 0 12px 0'
        },
        mainSubtitle: {
            fontSize: '16px',
            color: '#6b7280',
            margin: 0,
            maxWidth: '600px'
        },
        cardsContainer: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '24px',
            width: '100%',
            maxWidth: '900px'
        },
        card: {
            background: '#ffffff',
            borderRadius: '16px',
            padding: '28px',
            border: '2px solid #e5e7eb',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            textAlign: 'center' as const
        },
        cardHover: {
            borderColor: '#667eea',
            boxShadow: '0 12px 40px rgba(102, 126, 234, 0.15)',
            transform: 'translateY(-4px)'
        },
        iconContainer: {
            width: '64px',
            height: '64px',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '16px',
            fontSize: '28px'
        },
        reachIconBg: {
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
            color: '#ffffff'
        },
        geoIconBg: {
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: '#ffffff'
        },
        routeIconBg: {
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: '#ffffff'
        },
        cardLabel: {
            fontSize: '11px',
            fontWeight: 600,
            textTransform: 'uppercase' as const,
            letterSpacing: '1px',
            marginBottom: '8px'
        },
        reachLabel: {
            color: '#8b5cf6'
        },
        geoLabel: {
            color: '#10b981'
        },
        routeLabel: {
            color: '#3b82f6'
        },
        cardTitle: {
            fontSize: '18px',
            fontWeight: 700,
            color: '#1f2937',
            margin: '0 0 10px 0'
        },
        cardDescription: {
            fontSize: '13px',
            color: '#6b7280',
            margin: '0 0 16px 0',
            lineHeight: 1.5
        },
        featureList: {
            listStyle: 'none',
            padding: 0,
            margin: '0 0 20px 0',
            width: '100%'
        },
        featureItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '12px',
            color: '#374151',
            padding: '4px 0',
            justifyContent: 'flex-start',
            textAlign: 'left' as const
        },
        featureIcon: {
            color: '#10b981',
            flexShrink: 0
        },
        cardButton: {
            padding: '10px 24px',
            fontSize: '13px',
            fontWeight: 600,
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.2s',
            marginTop: 'auto'
        },
        reachButton: {
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
            color: '#ffffff'
        },
        geoButton: {
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: '#ffffff'
        },
        routeButton: {
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: '#ffffff'
        }
    };

    const [hoveredCard, setHoveredCard] = useState<string | null>(null);

    const renderEmptyState = () => (
        <div style={emptyStateStyles.wrapper}>
            {/* Header */}
            <div style={emptyStateStyles.header}>
                <h1 style={emptyStateStyles.mainTitle}>Launch Your First Campaign</h1>
                <p style={emptyStateStyles.mainSubtitle}>
                    Campaigns help you reach your audience effectively. Choose the type that best fits your advertising goals.
                </p>
            </div>

            {/* Campaign Option Cards */}
            <div style={emptyStateStyles.cardsContainer}>
                {/* Target Reach Card */}
                <div
                    style={{
                        ...emptyStateStyles.card,
                        ...(hoveredCard === 'reach' ? emptyStateStyles.cardHover : {})
                    }}
                    onMouseEnter={function() { setHoveredCard('reach'); }}
                    onMouseLeave={function() { setHoveredCard(null); }}
                >
                    <div style={{ ...emptyStateStyles.iconContainer, ...emptyStateStyles.reachIconBg }}>
                        <UsersIcon />
                    </div>
                    <span style={{ ...emptyStateStyles.cardLabel, ...emptyStateStyles.reachLabel }}>
                        Audience Based
                    </span>
                    <h3 style={emptyStateStyles.cardTitle}>Target Reach</h3>
                    <p style={emptyStateStyles.cardDescription}>
                        Reach a specific number of customers with your ads.
                    </p>
                    <ul style={emptyStateStyles.featureList}>
                        <li style={emptyStateStyles.featureItem}>
                            <CheckCircleIcon style={emptyStateStyles.featureIcon} />
                            <span>Set target impressions</span>
                        </li>
                        <li style={emptyStateStyles.featureItem}>
                            <CheckCircleIcon style={emptyStateStyles.featureIcon} />
                            <span>Optimize delivery</span>
                        </li>
                        <li style={emptyStateStyles.featureItem}>
                            <CheckCircleIcon style={emptyStateStyles.featureIcon} />
                            <span>Track progress</span>
                        </li>
                    </ul>
                    <button
                        style={{ ...emptyStateStyles.cardButton, ...emptyStateStyles.reachButton }}
                        onClick={function() { handleCreateCampaignClick('TargetReachCampaign'); }}
                    >
                        Create Reach Campaign
                    </button>
                </div>

                {/* Geo Fence Card */}
                <div
                    style={{
                        ...emptyStateStyles.card,
                        ...(hoveredCard === 'geo' ? emptyStateStyles.cardHover : {})
                    }}
                    onMouseEnter={function() { setHoveredCard('geo'); }}
                    onMouseLeave={function() { setHoveredCard(null); }}
                >
                    <div style={{ ...emptyStateStyles.iconContainer, ...emptyStateStyles.geoIconBg }}>
                        <MapMarkerIcon />
                    </div>
                    <span style={{ ...emptyStateStyles.cardLabel, ...emptyStateStyles.geoLabel }}>
                        Location Based
                    </span>
                    <h3 style={emptyStateStyles.cardTitle}>Geo Fence</h3>
                    <p style={emptyStateStyles.cardDescription}>
                        Target users within a specific location radius.
                    </p>
                    <ul style={emptyStateStyles.featureList}>
                        <li style={emptyStateStyles.featureItem}>
                            <CheckCircleIcon style={emptyStateStyles.featureIcon} />
                            <span>Set location radius</span>
                        </li>
                        <li style={emptyStateStyles.featureItem}>
                            <CheckCircleIcon style={emptyStateStyles.featureIcon} />
                            <span>Geographic targeting</span>
                        </li>
                        <li style={emptyStateStyles.featureItem}>
                            <CheckCircleIcon style={emptyStateStyles.featureIcon} />
                            <span>Location-based triggers</span>
                        </li>
                    </ul>
                    <button
                        style={{ ...emptyStateStyles.cardButton, ...emptyStateStyles.geoButton }}
                        onClick={function() { handleCreateCampaignClick('GeoFenceCampaign'); }}
                    >
                        Create Geo Campaign
                    </button>
                </div>

                {/* Draw Routes Card */}
                <div
                    style={{
                        ...emptyStateStyles.card,
                        ...(hoveredCard === 'route' ? emptyStateStyles.cardHover : {})
                    }}
                    onMouseEnter={function() { setHoveredCard('route'); }}
                    onMouseLeave={function() { setHoveredCard(null); }}
                >
                    <div style={{ ...emptyStateStyles.iconContainer, ...emptyStateStyles.routeIconBg }}>
                        <RouteIcon />
                    </div>
                    <span style={{ ...emptyStateStyles.cardLabel, ...emptyStateStyles.routeLabel }}>
                        Route Based
                    </span>
                    <h3 style={emptyStateStyles.cardTitle}>Draw Route</h3>
                    <p style={emptyStateStyles.cardDescription}>
                        Draw a custom route on the map for targeted advertising.
                    </p>
                    <ul style={emptyStateStyles.featureList}>
                        <li style={emptyStateStyles.featureItem}>
                            <CheckCircleIcon style={emptyStateStyles.featureIcon} />
                            <span>Draw custom routes</span>
                        </li>
                        <li style={emptyStateStyles.featureItem}>
                            <CheckCircleIcon style={emptyStateStyles.featureIcon} />
                            <span>Road-snapped paths</span>
                        </li>
                        <li style={emptyStateStyles.featureItem}>
                            <CheckCircleIcon style={emptyStateStyles.featureIcon} />
                            <span>Multiple waypoints</span>
                        </li>
                    </ul>
                    <button
                        style={{ ...emptyStateStyles.cardButton, ...emptyStateStyles.routeButton }}
                        onClick={function() { handleCreateCampaignClick('DrawRoutesCampaign'); }}
                    >
                        Create Route Campaign
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <PageSection style={{ minHeight: '100%', background: '#f8fafc' }}>
            <div style={styles.container}>
                {/* Header - only show when there are campaigns */}
                {campaignState.length > 0 && (
                    <div style={styles.header}>
                        <div>
                            <h1 style={styles.title}>Campaigns</h1>
                            <p style={styles.subtitle}>
                                {`${campaignState.length} campaign${campaignState.length !== 1 ? 's' : ''} in your library`}
                            </p>
                        </div>
                        <CreateCampaign triggerRefresh={setTriggerRefresh} existingCampaigns={campaignState} onCampaignCreated={function(id) { navigate('/campaigns/' + id); }} />
                    </div>
                )}

                {/* Hidden modal trigger for empty state */}
                <CreateCampaign
                    triggerRefresh={setTriggerRefresh}
                    isOpen={createModalOpen}
                    onOpenChange={function(open) { setCreateModalOpen(open); if (!open) setInitialCampaignType(undefined); }}
                    hideButton={true}
                    initialCampaignType={initialCampaignType}
                    existingCampaigns={campaignState}
                    onCampaignCreated={function(id) { navigate('/campaigns/' + id); }}
                />

                {/* Search and Filter Bar */}
                {campaignState.length > 0 && (
                    <div style={styles.searchContainer}>
                        <div style={styles.searchRow}>
                            {/* Search Input */}
                            <div style={styles.searchInputWrapper}>
                                <span style={styles.searchIcon}>
                                    <SearchIcon />
                                </span>
                                <input
                                    type="text"
                                    placeholder="Search by campaign name or organization..."
                                    value={searchText}
                                    onChange={function(e) { setSearchText(e.target.value); }}
                                    style={styles.searchInput}
                                />
                                {searchText && (
                                    <button
                                        style={styles.clearButton}
                                        onClick={function() { setSearchText(''); }}
                                        type="button"
                                    >
                                        <TimesIcon style={{ width: '12px', height: '12px' }} />
                                    </button>
                                )}
                            </div>

                            {/* Type Filter Chips */}
                            <div style={styles.filterChips}>
                                <button
                                    style={typeFilter === null ? styles.filterChipActive : styles.filterChip}
                                    onClick={function() { setTypeFilter(null); }}
                                    type="button"
                                >
                                    All Types
                                </button>
                                {campaignTypeOptions.map(function(option) {
                                    return (
                                        <button
                                            key={option.value}
                                            style={typeFilter === option.value ? styles.filterChipActive : styles.filterChip}
                                            onClick={function() { setTypeFilter(typeFilter === option.value ? null : option.value); }}
                                            type="button"
                                        >
                                            {option.label}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* State Filter Dropdown */}
                            <div style={styles.stateDropdownContainer} ref={stateDropdownRef}>
                                <button
                                    style={{
                                        ...styles.stateDropdownButton,
                                        ...(stateFilters.length > 0 || showStateDropdown ? styles.stateDropdownButtonActive : {})
                                    }}
                                    onClick={function() { setShowStateDropdown(!showStateDropdown); }}
                                    type="button"
                                >
                                    <span>State{stateFilters.length > 0 ? ' (' + stateFilters.length + ')' : ''}</span>
                                    <span style={{ fontSize: '10px' }}>{showStateDropdown ? '\u25B2' : '\u25BC'}</span>
                                </button>
                                {showStateDropdown && (
                                    <div style={styles.stateDropdownMenu}>
                                        {campaignStateOptions.map(function(option) {
                                            var isChecked = stateFilters.indexOf(option.value) !== -1;
                                            return (
                                                <div
                                                    key={option.value}
                                                    style={styles.stateDropdownItem}
                                                    onClick={function() { toggleStateFilter(option.value); }}
                                                    onMouseEnter={function(e) { (e.currentTarget as HTMLElement).style.background = '#f3f4f6'; }}
                                                    onMouseLeave={function(e) { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                                                >
                                                    <div style={{
                                                        ...styles.stateCheckbox,
                                                        ...(isChecked ? styles.stateCheckboxChecked : {})
                                                    }}>
                                                        {isChecked && <span style={{ fontSize: '10px' }}>{'\u2713'}</span>}
                                                    </div>
                                                    <span>{option.label}</span>
                                                </div>
                                            );
                                        })}
                                        {stateFilters.length > 0 && (
                                            <div
                                                style={{...styles.stateDropdownItem, borderTop: '1px solid #e5e7eb', marginTop: '4px', paddingTop: '12px', color: '#667eea', fontWeight: 500}}
                                                onClick={function() { setStateFilters([]); }}
                                                onMouseEnter={function(e) { (e.currentTarget as HTMLElement).style.background = '#f3f4f6'; }}
                                                onMouseLeave={function(e) { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                                            >
                                                Clear all
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Results info */}
                        {(searchText || typeFilter || stateFilters.length > 0) && (
                            <p style={styles.resultsInfo}>
                                Showing {filteredCampaigns.length} of {campaignState.length} campaign{campaignState.length !== 1 ? 's' : ''}
                                {searchText && <span> matching "{searchText}"</span>}
                                {typeFilter && <span> with type {campaignTypeOptions.find(function(o) { return o.value === typeFilter; })?.label || typeFilter}</span>}
                                {stateFilters.length > 0 && <span> with state{stateFilters.length > 1 ? 's' : ''} "{stateFilters.map(function(s) { var opt = campaignStateOptions.find(function(o) { return o.value === s; }); return opt ? opt.label : s; }).join(', ')}"</span>}
                            </p>
                        )}
                    </div>
                )}

                {/* Campaign Cards Grid */}
                {isLoading ? (
                    <div style={styles.emptyStateContainer}>
                        <p style={{ color: '#6b7280' }}>Loading campaigns...</p>
                    </div>
                ) : campaignState.length === 0 ? (
                    renderEmptyState()
                ) : filteredCampaigns.length === 0 ? (
                    <div style={styles.emptyStateContainer}>
                        <EmptyState variant={EmptyStateVariant.large}>
                            <EmptyStateIcon icon={SearchIcon} />
                            <Title headingLevel="h4" size="lg">
                                No Results Found
                            </Title>
                            <EmptyStateBody>
                                No campaigns match your search criteria. Try adjusting your filters.
                            </EmptyStateBody>
                        </EmptyState>
                    </div>
                ) : (
                    <div style={styles.grid}>
                        {filteredCampaigns.map(function(campaign) {
                            return (
                                <CampaignCard
                                    key={campaign.campaignId}
                                    campaign={campaign}
                                    onEditClick={handleEditClick}
                                    onCardClick={handleCardClick}
                                    onDeleteClick={handleDeleteClick}
                                    onStopClick={handleStopClick}
                                />
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Edit Campaign Modal */}
            <EditCampaign
                campaign={selectedCampaign}
                isOpen={editModalOpen}
                onClose={handleEditClose}
                onSave={handleEditSave}
            />

            {/* Error Modal */}
            {showErrorModal && (
                <div style={styles.errorOverlay} onClick={() => setShowErrorModal(false)}>
                    <div style={styles.errorModal} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.errorIcon}>
                            <ExclamationCircleIcon style={{ fontSize: '24px' }} />
                        </div>
                        <h3 style={styles.errorTitle}>Error</h3>
                        <p style={styles.errorText}>{errorMessage}</p>
                        <button
                            style={styles.errorButton}
                            onClick={() => setShowErrorModal(false)}
                        >
                            OK
                        </button>
                    </div>
                </div>
            )}

            {/* Create Ad First Modal */}
            {showCreateAdModal && (
                <div style={styles.createAdOverlay} onClick={() => setShowCreateAdModal(false)}>
                    <div style={styles.createAdModal} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.createAdHeader}>
                            <div style={styles.createAdIconContainer}>
                                <PlusCircleIcon />
                            </div>
                            <h2 style={styles.createAdTitle}>Create an Ad First</h2>
                        </div>
                        <div style={styles.createAdBody}>
                            <p style={styles.createAdText}>
                                Before creating a campaign, you need to have at least one ad ready.
                                Head over to the Ads page to create your first ad, then come back to launch your campaign.
                            </p>
                            <Link
                                to="/ads"
                                style={styles.createAdLink}
                                onClick={() => setShowCreateAdModal(false)}
                            >
                                <PlusCircleIcon />
                                Go to Ads
                            </Link>
                            <button
                                style={styles.createAdCancel}
                                onClick={() => setShowCreateAdModal(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </PageSection>
    );
};

export { Campaigns };
